import React, { useState, useRef, useCallback, useEffect } from "react";
import type { Contact } from "./types";
import type {
  ImportPlan,
  AddAction,
  MergeAction,
  SkipAction,
  ProgressUpdate,
} from "../types/importTypes";

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB - generous limit since users are trusted

type ImportMode = "text" | "csv" | "image";

interface ImportContactsViewProps {
  accessToken: string;
  existingContacts: Contact[];
  onImportComplete: (newContacts: Contact[], updatedContacts: Contact[]) => void;
}

const ImportContactsView: React.FC<ImportContactsViewProps> = ({
  accessToken,
  existingContacts,
  onImportComplete,
}) => {
  // Import mode selection
  const [importMode, setImportMode] = useState<ImportMode>("text");

  // Input state
  const [textInput, setTextInput] = useState("");
  const [csvContent, setCsvContent] = useState("");
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [imageContent, setImageContent] = useState<string | null>(null); // base64
  const [imageFileName, setImageFileName] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [sourceDescription, setSourceDescription] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<ProgressUpdate | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Import plan state
  const [importPlan, setImportPlan] = useState<ImportPlan | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Handle Escape key to reset view when not processing
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isProcessing && !isConfirming && importPlan) {
        setImportPlan(null);
        setProgress(null);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isProcessing, isConfirming, importPlan]);

  // Handle file selection with size validation
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum is 5MB.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setCsvFileName(file.name);
    setError(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setCsvContent(content);
    };
    reader.onerror = () => {
      setError("Failed to read the file. Please try again.");
    };
    reader.readAsText(file);
  }, []);

  // Clear file selection
  const handleClearFile = useCallback(() => {
    setCsvContent("");
    setCsvFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  // Handle image file selection
  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`Image too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum is 5MB.`);
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file (PNG, JPG, etc.).");
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
      return;
    }

    setImageFileName(file.name);
    setError(null);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(previewUrl);

    // Read as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      // Remove the data:image/...;base64, prefix for the API
      setImageContent(base64);
    };
    reader.onerror = () => {
      setError("Failed to read the image. Please try again.");
    };
    reader.readAsDataURL(file);
  }, []);

  // Clear image selection
  const handleClearImage = useCallback(() => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImageContent(null);
    setImageFileName(null);
    setImagePreviewUrl(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  }, [imagePreviewUrl]);

  // Generate import plan
  const handleGeneratePlan = useCallback(async () => {
    // Validate based on selected mode
    if (importMode === "text" && !textInput.trim()) {
      setError("Please enter contact information.");
      return;
    }
    if (importMode === "csv" && !csvContent.trim()) {
      setError("Please upload a CSV file.");
      return;
    }
    if (importMode === "image" && !imageContent) {
      setError("Please upload an image.");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setImportPlan(null);
    setProgress({ stage: "preparing", percent: 10, message: "Preparing data..." });

    try {
      // Prepare key fields from existing contacts for duplicate detection
      const existingContactsSummary = existingContacts.map((c) => ({
        contact_id: c.contact_id,
        full_name: c.full_name,
        first_name: c.first_name,
        last_name: c.last_name,
        email: c.email,
        mobile_phone: c.mobile_phone,
        home_phone: c.home_phone,
        work_phone: c.work_phone,
      }));

      setProgress({ stage: "ai", percent: 50, message: "Analyzing contacts with AI..." });

      // Only send data for the selected import mode
      const response = await fetch("/api/contacts/import-preview", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          textInput: importMode === "text" ? textInput.trim() : undefined,
          csvContent: importMode === "csv" ? csvContent.trim() : undefined,
          imageContent: importMode === "image" ? imageContent : undefined,
          sourceDescription: sourceDescription.trim() || undefined,
          existingContacts: existingContactsSummary,
        }),
      });

      // Handle 401 specially for token refresh
      if (response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const plan = await response.json();
      setImportPlan(plan);
      setProgress({ stage: "complete", percent: 100, message: "Import plan ready!" });
    } catch (err) {
      console.error("Error generating import plan:", err);
      if (err instanceof Error && err.message === "UNAUTHORIZED") {
        setError("Your session has expired. Please refresh the page and sign in again.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to generate import plan.");
      }
    } finally {
      setIsProcessing(false);
    }
  }, [
    importMode,
    textInput,
    csvContent,
    imageContent,
    sourceDescription,
    existingContacts,
    accessToken,
  ]);

  // Confirm and execute import
  const handleConfirmImport = useCallback(async () => {
    if (!importPlan) return;

    setIsConfirming(true);
    setError(null);

    try {
      const response = await fetch("/api/contacts/import-execute", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ plan: importPlan }),
      });

      // Handle 401 specially for token refresh
      if (response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();
      onImportComplete(result.addedContacts || [], result.updatedContacts || []);

      // Reset form after successful import
      setTextInput("");
      setCsvContent("");
      setCsvFileName(null);
      setSourceDescription("");
      setImportPlan(null);
      setProgress(null);
    } catch (err) {
      console.error("Error executing import:", err);
      if (err instanceof Error && err.message === "UNAUTHORIZED") {
        setError("Your session has expired. Please refresh the page and sign in again.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to execute import.");
      }
    } finally {
      setIsConfirming(false);
    }
  }, [importPlan, accessToken, onImportComplete]);

  // Reset to editing mode
  const handleEditPlan = useCallback(() => {
    setImportPlan(null);
    setProgress(null);
  }, []);

  const hasInput =
    importMode === "text"
      ? textInput.trim()
      : importMode === "csv"
        ? csvContent.trim()
        : imageContent;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="pb-4">
        <h2 className="text-lg font-semibold text-white">Import Contacts</h2>
        <p className="text-sm text-text-secondary mt-0.5">
          {importMode === "text"
            ? "Type or paste contact information. AI will extract and deduplicate contacts."
            : importMode === "csv"
              ? "Upload a CSV file. AI will extract and deduplicate contacts."
              : "Upload a business card or image. AI will extract contact details."}
        </p>
      </div>

      {/* Import Mode Selector */}
      {!importPlan && (
        <div className="pb-4">
          <div className="inline-flex rounded bg-navy/80 border border-border-strong p-1">
            <button
              onClick={() => setImportMode("text")}
              disabled={isProcessing}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                importMode === "text"
                  ? "bg-rose-dim text-rose-accent shadow-sm"
                  : "text-text-secondary hover:text-text-bright"
              } disabled:opacity-50`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Type Information
            </button>
            <button
              onClick={() => setImportMode("csv")}
              disabled={isProcessing}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                importMode === "csv"
                  ? "bg-rose-dim text-rose-accent shadow-sm"
                  : "text-text-secondary hover:text-text-bright"
              } disabled:opacity-50`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload CSV
            </button>
            <button
              onClick={() => setImportMode("image")}
              disabled={isProcessing}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                importMode === "image"
                  ? "bg-rose-dim text-rose-accent shadow-sm"
                  : "text-text-secondary hover:text-text-bright"
              } disabled:opacity-50`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              Business Card
            </button>
          </div>
        </div>
      )}

      {/* Action buttons - fixed above scroll area when plan exists */}
      {importPlan && (
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-navy max-w-4xl">
          <button
            onClick={handleEditPlan}
            disabled={isConfirming}
            className="flex items-center gap-2 px-5 py-2.5 rounded bg-navy-mid hover:bg-navy-light text-text-primary font-medium border border-border-strong transition-colors disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 17l-5-5m0 0l5-5m-5 5h12"
              />
            </svg>
            Edit Input
          </button>
          <button
            onClick={handleConfirmImport}
            disabled={
              isConfirming ||
              (importPlan.summary.add_count === 0 && importPlan.summary.merge_count === 0)
            }
            className="flex items-center gap-2 px-6 py-3 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConfirming ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Confirm Import ({importPlan.summary.add_count + importPlan.summary.merge_count}{" "}
                contacts)
              </>
            )}
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {!importPlan ? (
          // Input Mode
          <div className="space-y-6 max-w-4xl">
            {/* Text Input - only shown in text mode */}
            {importMode === "text" && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Paste or type contact information
                </label>
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Enter contacts in any format:
- Name, email, phone
- Business cards text
- Contact lists
- Free-form descriptions like 'John Smith from ABC Corp, his number is 0412 345 678'

The AI will extract contact details automatically."
                  className="w-full h-56 px-4 py-3 rounded bg-navy/80 border border-border-strong text-white text-sm placeholder-text-muted focus:outline-none focus:border-rose-accent/40 focus:ring-2 focus:ring-rose-accent/15 transition-all resize-none"
                  disabled={isProcessing}
                />
              </div>
            )}

            {/* CSV Upload - only shown in csv mode */}
            {importMode === "csv" && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Upload a CSV file
                </label>
                <div className="flex items-center gap-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={isProcessing}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessing}
                    className="flex items-center gap-2 px-4 py-2.5 rounded bg-navy border border-border-strong text-text-primary hover:border-border-strong hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    Choose File
                  </button>
                  {csvFileName && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-rose-glow border border-border-rose">
                      <svg
                        className="w-4 h-4 text-rose-accent"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm text-rose-accent">{csvFileName}</span>
                      <button
                        onClick={handleClearFile}
                        disabled={isProcessing}
                        className="p-1 rounded hover:bg-rose-dim transition-colors"
                      >
                        <svg
                          className="w-3 h-3 text-rose-accent"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                {csvContent && (
                  <div className="mt-3 p-3 rounded-sm bg-navy/50 border border-border-strong">
                    <p className="text-xs text-text-muted mb-2">CSV Preview (first 500 chars):</p>
                    <pre className="text-xs text-text-secondary whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                      {csvContent.slice(0, 500)}
                      {csvContent.length > 500 && "..."}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Image Upload - only shown in image mode */}
            {importMode === "image" && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Upload a business card or image
                </label>
                <div className="flex items-start gap-4">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    disabled={isProcessing}
                  />
                  <button
                    onClick={() => imageInputRef.current?.click()}
                    disabled={isProcessing}
                    className="flex items-center gap-2 px-4 py-2.5 rounded bg-navy border border-border-strong text-text-primary hover:border-border-strong hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Choose Image
                  </button>
                  {imageFileName && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-rose-glow border border-border-rose">
                      <svg
                        className="w-4 h-4 text-rose-accent"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm text-rose-accent">{imageFileName}</span>
                      <button
                        onClick={handleClearImage}
                        disabled={isProcessing}
                        className="p-1 rounded hover:bg-rose-dim transition-colors"
                      >
                        <svg
                          className="w-3 h-3 text-rose-accent"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                {imagePreviewUrl && (
                  <div className="mt-3 p-3 rounded-sm bg-navy/50 border border-border-strong">
                    <p className="text-xs text-text-muted mb-2">Image Preview:</p>
                    <img
                      src={imagePreviewUrl}
                      alt="Business card preview"
                      className="max-w-md max-h-64 rounded-sm object-contain"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Source Description - shown for all modes */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Source description
                <span className="text-text-muted font-normal ml-1">(optional)</span>
              </label>
              <input
                type="text"
                value={sourceDescription}
                onChange={(e) => setSourceDescription(e.target.value)}
                placeholder={
                  importMode === "text"
                    ? "e.g., 'Business cards from Melbourne conference'"
                    : importMode === "csv"
                      ? "e.g., 'LinkedIn connections export'"
                      : "e.g., 'Business card from networking event'"
                }
                className="w-full px-4 py-2.5 rounded bg-navy/80 border border-border-strong text-white text-sm placeholder-text-muted focus:outline-none focus:border-rose-accent/40 focus:ring-2 focus:ring-rose-accent/15 transition-all"
                disabled={isProcessing}
              />
              <p className="text-xs text-text-muted mt-1.5">
                Describe where these contacts are from. This helps with organization and filtering.
              </p>
            </div>

            {/* Progress indicator with warning */}
            {isProcessing && progress && (
              <div className="p-4 rounded bg-navy/50 border border-border-strong">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-5 h-5 border-2 border-rose-accent/30 border-t-rose-accent rounded-full animate-spin" />
                  <span className="text-sm text-text-primary">{progress.message}</span>
                </div>
                <div className="h-2 bg-navy-mid rounded-full overflow-hidden">
                  <div
                    className="h-full bg-rose-accent transition-all duration-300"
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                <p className="text-xs text-text-muted mt-2">
                  Please do not close this page while processing...
                </p>
              </div>
            )}

            {/* Error display */}
            {error && (
              <div className="p-4 rounded bg-red-500/10 border border-red-500/30">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              </div>
            )}

            {/* Generate button */}
            <div className="pt-2">
              <button
                onClick={handleGeneratePlan}
                disabled={!hasInput || isProcessing}
                className="flex items-center gap-2 px-6 py-3 rounded bg-rose-accent hover:bg-rose-bright text-navy-darkest font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    Generate Import Plan
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          // Import Plan Display
          <div className="max-w-4xl">
            <ImportPlanDisplay plan={importPlan} />

            {/* Error display in plan view */}
            {error && (
              <div className="mt-6 p-4 rounded-sm bg-red-500/10 border border-red-500/30">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Import Plan Display Component
const ImportPlanDisplay: React.FC<{ plan: ImportPlan }> = ({ plan }) => {
  const addActions = plan.actions.filter((a): a is AddAction => a.action === "ADD");
  const mergeActions = plan.actions.filter((a): a is MergeAction => a.action === "MERGE");
  const skipActions = plan.actions.filter((a): a is SkipAction => a.action === "SKIP");

  return (
    <div className="space-y-6">
      {/* Source Name */}
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
        </svg>
        <span>
          Source: <span className="text-text-primary">{plan.sourceName}</span>
        </span>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded bg-emerald-500/10 border border-emerald-500/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-emerald-500/20 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-emerald-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-emerald-400">{plan.summary.add_count}</p>
              <p className="text-sm text-text-secondary">New contacts</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded bg-amber-500/10 border border-amber-500/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-amber-500/20 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-amber-400">{plan.summary.merge_count}</p>
              <p className="text-sm text-text-secondary">To merge</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded bg-navy-mid/20 border border-border-strong">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-navy-mid/30 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-text-secondary">{plan.summary.skip_count}</p>
              <p className="text-sm text-text-muted">Skipped</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Actions */}
      {addActions.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            New Contacts to Add ({addActions.length})
          </h3>
          <div className="space-y-2">
            {addActions.map((action, index) => (
              <div
                key={index}
                className="p-3 rounded-sm bg-navy/50 border border-border-strong hover:border-emerald-500/30 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">
                      {action.contact.full_name ||
                        `${action.contact.first_name || ""} ${action.contact.last_name || ""}`.trim() ||
                        "Unknown"}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      {action.contact.email && (
                        <span className="text-xs text-text-secondary">{action.contact.email}</span>
                      )}
                      {action.contact.mobile_phone && (
                        <span className="text-xs text-text-muted">
                          {action.contact.mobile_phone}
                        </span>
                      )}
                    </div>
                    {action.contact.notes && (
                      <p className="text-xs text-text-muted mt-1 line-clamp-2">
                        {action.contact.notes}
                      </p>
                    )}
                  </div>
                  <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded ml-2">
                    ADD
                  </span>
                </div>
                <p className="text-xs text-text-muted mt-2 italic">{action.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Merge Actions */}
      {mergeActions.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Existing Contacts to Merge ({mergeActions.length})
          </h3>
          <div className="space-y-2">
            {mergeActions.map((action, index) => (
              <div
                key={index}
                className="p-3 rounded-sm bg-navy/50 border border-border-strong hover:border-amber-500/30 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">{action.existing_contact_name}</p>
                    <p className="text-xs text-text-muted italic">{action.reason}</p>
                  </div>
                  <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded ml-2">
                    MERGE
                  </span>
                </div>
                {/* Detailed field changes */}
                {((action.field_changes && action.field_changes.length > 0) || plan.sourceName) && (
                  <div className="mt-2 space-y-1">
                    {action.field_changes && action.field_changes.map((change, changeIndex) => (
                      <div key={changeIndex} className="text-xs flex items-start gap-2">
                        <span className="text-text-muted w-20 flex-shrink-0 capitalize">
                          {change.label}:
                        </span>
                        {change.oldValue ? (
                          <span className="flex-1 min-w-0">
                            <span className="text-red-400/70 line-through">{change.oldValue}</span>
                            <span className="text-navy-light mx-1">→</span>
                            <span className="text-green-400">{change.newValue}</span>
                          </span>
                        ) : (
                          <span className="text-green-400">+ {change.newValue}</span>
                        )}
                      </div>
                    ))}
                    {plan.sourceName && (
                      <div className="text-xs flex items-start gap-2">
                        <span className="text-text-muted w-20 shrink-0">source:</span>
                        <span className="text-green-400">+ {plan.sourceName}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skip Actions */}
      {skipActions.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-text-muted mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
            Skipped Items ({skipActions.length})
          </h3>
          <div className="space-y-2">
            {skipActions.map((action, index) => (
              <div key={index} className="p-3 rounded-sm bg-navy/30 border border-navy">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-text-muted line-clamp-2">{action.original_data}</p>
                  </div>
                  <span className="px-2 py-0.5 text-xs bg-navy-mid text-text-muted rounded ml-2">
                    SKIP
                  </span>
                </div>
                <p className="text-xs text-navy-light mt-2 italic">{action.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportContactsView;
